import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle, Calendar, Sparkles, Rocket, Users } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PricingSection from "@/components/sections/PricingSection";
import OneTimePricingSection from "@/components/sections/OneTimePricingSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PRICING_ITEMLIST_SCHEMA, SITE } from "@/lib/seo";

const TITLE = "Precios y Planes desde 199€/mes | Vibra Marketing";
const DESCRIPTION =
  "Planes de marketing digital sin permanencia: 1 servicio 199€, 2 servicios 490€ y 4 servicios 1.290€/mes IVA incluido. Servicios puntuales desde 500€ en 3 cuotas sin intereses.";
const URL = `${SITE.url}/precios`;

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE.url}/` },
    { "@type": "ListItem", position: 2, name: "Precios", item: URL },
  ],
};

const faqs = [
  {
    q: "¿Los precios incluyen IVA?",
    a: "Sí. Todos los precios mostrados incluyen IVA. Junto a cada precio verás también el desglose sin IVA.",
  },
  {
    q: "¿Hay permanencia?",
    a: "No. Todos los planes son cancelables mes a mes. Mantienes el servicio hasta el final del ciclo pagado.",
  },
  {
    q: "¿Cuándo empieza mi ciclo?",
    a: "El día que pagas. Se renueva automáticamente cada mes en esa fecha.",
  },
  {
    q: "¿Puedo cambiar de servicios o de plan?",
    a: "Sí, los cambios se aplican al inicio del siguiente ciclo de facturación.",
  },
  {
    q: "¿Cómo funcionan las 3 cuotas sin intereses?",
    a: "Los servicios puntuales se pueden pagar en 3 plazos con Klarna. Ejemplo: un CRM de 900€ son 3 pagos de 300€.",
  },
  {
    q: "¿La inversión publicitaria está incluida?",
    a: "No: el presupuesto de anuncios lo pagas tú directamente a Meta, TikTok o Google desde tu cuenta. Recomendamos mínimo 300€/mes en Meta/TikTok y 10€/día en Google. Nosotros gestionamos y optimizamos las campañas.",
  },
  {
    q: "¿Qué pasa justo después de contratar?",
    a: "Recibes el acceso a nuestro onboarding, agendamos tu primera reunión y en los primeros 14 días dejamos tu setup funcionando.",
  },
  {
    q: "¿Qué pasa con mis cuentas si me voy?",
    a: "Todo queda en tu poder: las cuentas, campañas, píxel y datos son siempre de tu titularidad. Solo retiramos nuestro acceso de gestor.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const comparisonRows = [
  { label: "Precio/mes (IVA incl.)", basic: "199€", pro: "490€", partner: "1.290€" },
  { label: "Servicios recurrentes", basic: "1 a elegir", pro: "2 a elegir", partner: "4 a elegir" },
  { label: "Coordinación", basic: "Equipo de producción", pro: "Coordinador de cuenta fijo", partner: "Project manager y equipo dedicado" },
  { label: "Reuniones", basic: "—", pro: "—", partner: "Reunión semanal" },
  { label: "Urgencias", basic: "—", pro: "Prioridad sobre el plan de 1 servicio", partner: "En 48 horas" },
  { label: "Permanencia", basic: "Sin permanencia", pro: "Sin permanencia", partner: "Sin permanencia" },
  { label: "Cancelación", basic: "Cuando quieras", pro: "Cuando quieras", partner: "Cuando quieras" },
  { label: "Soporte", basic: "Plataforma Vibra", pro: "Plataforma Vibra", partner: "Plataforma Vibra" },
];

const profiles = [
  {
    icon: Sparkles,
    title: "¿Estás empezando o quieres probar un canal?",
    plan: "1 servicio",
    text: "Un servicio bien ejecutado rinde más que cinco a medias.",
  },
  {
    icon: Rocket,
    title: "¿Tu negocio ya factura y quieres crecer en varios frentes?",
    plan: "2 servicios",
    text: "Dos servicios coordinados por una misma persona.",
    highlight: true,
  },
  {
    icon: Users,
    title: "¿Quieres delegar tu marketing con project manager?",
    plan: "4 servicios",
    text: "Cuatro servicios funcionando como tu departamento de marketing externo.",
  },
];

const alternativesRows = [
  { label: "Coste mensual", emp: "2.200-3.000€ (salario + SS)", free: "1.000-2.000€", vibra: "1.290€ IVA incl." },
  { label: "Especialidades cubiertas", emp: "1-2", free: "1-2", vibra: "8 (equipo completo)" },
  { label: "Vacaciones y bajas", emp: "Sí, las cubres tú", free: "Depende", vibra: "Nunca te quedas sin servicio" },
  { label: "Compromiso", emp: "Contrato laboral", free: "Variable", vibra: "Sin permanencia" },
];

const Precios = () => (
  <div className="min-h-screen bg-background">
    <Helmet>
      <title>{TITLE}</title>
      <meta name="description" content={DESCRIPTION} />
      <link rel="canonical" href={URL} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={TITLE} />
      <meta property="og:description" content={DESCRIPTION} />
      <meta property="og:url" content={URL} />
      <meta property="og:image" content={SITE.ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(PRICING_ITEMLIST_SCHEMA)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </Helmet>
    <Header />
    <main className="pt-16">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="container px-6 pt-8 text-xs text-muted-foreground">
        <ol className="flex items-center gap-2">
          <li><Link to="/" className="hover:text-foreground">Inicio</Link></li>
          <li aria-hidden>/</li>
          <li className="text-foreground">Precios</li>
        </ol>
      </nav>

      {/* Hero + claim + badge chips */}
      <header className="container px-6 pt-10 pb-6 text-center max-w-3xl mx-auto">
        <h1 className="font-display text-4xl md:text-6xl uppercase mb-6 leading-[1.05]">
          Precios claros. <br />
          <span className="text-gradient">Lo que ves es lo que pagas.</span>
        </h1>
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {[
            "199€ – 1.290€/mes",
            "Sin permanencia",
            "IVA incluido",
            "3 cuotas sin intereses",
          ].map((chip) => (
            <span
              key={chip}
              className="px-4 py-1.5 rounded-full border border-primary/30 bg-accent text-foreground text-xs font-medium uppercase tracking-wider"
            >
              {chip}
            </span>
          ))}
        </div>
      </header>

      {/* 3 plans + 8 services grid */}
      <PricingSection hideHeader />

      {/* Comparative table */}
      <section className="container px-6 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-3">
              Comparativa de <span className="text-gradient">planes</span>
            </h2>
            <p className="text-muted-foreground">Todo lo que incluye cada plan, de un vistazo.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-[0_0_60px_-30px_hsl(var(--primary)/0.3)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-accent/40">
                  <th className="text-left p-4 font-semibold"></th>
                  <th className="p-4 font-semibold">1 servicio</th>
                  <th className="p-4 font-semibold bg-primary/10 text-primary">2 servicios</th>
                  <th className="p-4 font-semibold">4 servicios</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((r, i) => (
                  <tr key={r.label} className={i % 2 ? "bg-accent/20" : ""}>
                    <td className="p-4 font-medium text-foreground">{r.label}</td>
                    <td className="p-4 text-center text-muted-foreground">{r.basic}</td>
                    <td className="p-4 text-center text-foreground bg-primary/5 font-medium">{r.pro}</td>
                    <td className="p-4 text-center text-muted-foreground">{r.partner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Qué plan es para ti */}
      <section className="container px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-3">
              ¿Qué plan es <span className="text-gradient">para ti</span>?
            </h2>
            <p className="text-muted-foreground">Tres perfiles, tres respuestas claras.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {profiles.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.plan}
                  className={`rounded-2xl border p-6 transition-all hover:-translate-y-1 ${
                    p.highlight
                      ? "border-primary/40 bg-gradient-to-br from-primary/10 via-purple-500/5 to-transparent shadow-[0_0_60px_-25px_hsl(var(--primary)/0.4)]"
                      : "border-border bg-card"
                  }`}
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-medium text-foreground mb-2">{p.title}</p>
                  <p className="text-2xl font-display uppercase text-gradient mb-2">→ {p.plan}</p>
                  <p className="text-sm text-muted-foreground">{p.text}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-8 max-w-2xl mx-auto">
            Todos los servicios tienen el mismo alcance completo en cualquier plan: lo que
            cambia es cuántos eliges, no la profundidad de cada uno.
          </p>
        </div>
      </section>

      {/* One-time services */}
      <OneTimePricingSection />


      {/* Agency vs alternatives */}
      <section className="container px-6 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-3">
              ¿Cuánto cuesta hacer marketing de las{" "}
              <span className="text-gradient">tres formas posibles</span>?
            </h2>
            <p className="text-muted-foreground">
              Comparativa honesta entre contratar empleado, freelance o Vibra.
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-[0_0_60px_-30px_hsl(var(--primary)/0.3)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-accent/40">
                  <th className="text-left p-4 font-semibold"></th>
                  <th className="p-4 font-semibold">Empleado de marketing</th>
                  <th className="p-4 font-semibold">Freelance</th>
                  <th className="p-4 font-semibold bg-primary/10 text-primary">Vibra (4 servicios)</th>
                </tr>
              </thead>
              <tbody>
                {alternativesRows.map((r, i) => (
                  <tr key={r.label} className={i % 2 ? "bg-accent/20" : ""}>
                    <td className="p-4 font-medium text-foreground">{r.label}</td>
                    <td className="p-4 text-center text-muted-foreground">{r.emp}</td>
                    <td className="p-4 text-center text-muted-foreground">{r.free}</td>
                    <td className="p-4 text-center text-foreground bg-primary/5 font-medium">{r.vibra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-5xl uppercase mb-3">
              Preguntas sobre <span className="text-gradient">precios</span>
            </h2>
            <p className="text-muted-foreground">Lo que más nos preguntan antes de contratar.</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-medium">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container px-6 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-primary/10 via-purple-500/5 to-background border border-primary/20 px-6 py-14 md:py-16 text-center shadow-[0_0_60px_-20px_hsl(var(--primary)/0.3)]">
          <h2 className="font-display text-3xl md:text-5xl uppercase mb-4">
            ¿Hablamos de tu caso?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Agenda una llamada gratuita de 20 minutos y te decimos con honestidad qué plan
            encaja con tu negocio.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
            >
              <Calendar className="w-4 h-4" /> Agendar reunión
            </Link>
            <a
              href="https://wa.me/34614541275"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white font-medium transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Precios;
